import { ia } from 'lib/helpers/utility';
import { loadJsPdf } from 'lib/helpers/pdf/loaders';
import { GAP } from 'lib/helpers/pdf/tableExport/lib/constants';
import { generateHeader } from 'lib/helpers/pdf/tableExport/pdfHeader';
import { preloadHeaderImage } from 'lib/helpers/pdf/tableExport/lib/headerImage';
import { printPrintWindow } from 'lib/helpers/pdf/tableExport/lib/printPrintWindow';
import {
  drawHorizontalLine,
  generatePageNumberAndDate
} from 'lib/helpers/pdf/tableExport/lib/helpers';

import { buildFormattedActivities } from './formatActivities';
import {
  addAvatar,
  addBackgroundColor,
  addNewLineIfNeeded,
  addPageIfNeeded,
  drawVerticalLine,
  drawWrappedText,
  lineHeight,
  smallSpacing
} from './pdfDrawingUtils';

export const exportActivitiesToPdf = async ({
  groupedActivities,
  title = 'Activity log',
  category,
  headerConfigs,
  returnBytes = false,
  isViewerAdmin = false,
  timezone = null
}) => {
  const formattedActivities = buildFormattedActivities({
    groupedActivities,
    category,
    isViewerAdmin,
    timezone
  });

  if (!ia(formattedActivities)) return;

  const { jsPDF } = await loadJsPdf();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Set PDF metadata
  const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.setProperties({ title: fileName });

  await preloadHeaderImage(headerConfigs?.practice?.header_photo);

  // Generate header using the same utility as printPdf
  let startY = generateHeader({ doc, headerConfigs });
  startY += GAP / 2;

  // Add title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(title, GAP / 2, startY);
  startY += GAP / 1.5;

  for (let index = 0; index < formattedActivities.length; index += 1) {
    const activity = formattedActivities[index];

    // Check if we need a new page
    startY = addPageIfNeeded(doc, startY);

    // Add indentation for replies
    const replyIndent = activity.isReply ? GAP / 2 : 0;
    const leftMargin = GAP / 2 + replyIndent;

    // if there is only one change and it is a string, check if we need a new page before printing the comment
    if (activity?.changes?.length === 1 && typeof activity.changes[0] === 'string') {
      const availableWidth = pageWidth - leftMargin;

      const splitText = doc.splitTextToSize(activity.changes[0], availableWidth);
      const changeHeight = (splitText.length + 1) * lineHeight; // added 1 for header line

      // Check if we need a new page before printing the comment
      const pageHeight = doc.internal.pageSize.height;
      const minBottomMargin = 30; // footer height

      if (startY + changeHeight > pageHeight - minBottomMargin) {
        doc.addPage();
        startY = GAP;
      }
    }

    // Format: Date/Time, Actor, then changes
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    let startX = leftMargin;

    // add date group key
    if (activity.dateKey) {
      addBackgroundColor(doc, startY, pageWidth, lineHeight * 2);

      startY += lineHeight / 2;
      doc.setFont('helvetica', 'bold');
      doc.text(activity.dateKey, startX, startY);
      startY += GAP;

      continue;
    }

    if (!activity.isReply) {
      doc.text(activity.time, startX, startY);
    }
    const timeX = doc.getTextWidth(activity.time);
    startX += timeX + smallSpacing;

    // add avatar (image or initials) before the author text
    const avatarX =
      (await addAvatar(doc, startX, startY, activity.avatarImage, activity.avatarInitials)) || 0;

    // add Author Text to the left (with indentation for replies)
    doc.text(activity.authorText, startX + avatarX, startY);
    startY += GAP - lineHeight;

    doc.setFont('helvetica', 'normal');

    // Calculate available width for change text (accounting for reply indentation)
    const availableWidth = pageWidth - startX - leftMargin;

    const printChanges = (change) => {
      const changeCurrentX = startX + smallSpacing * 2;
      let currentX = changeCurrentX;

      // Check if we need a new page for this change
      startY = addPageIfNeeded(doc, startY);

      // For simple string changes (comments), render as before
      if (typeof change === 'string') {
        const splitText = doc.splitTextToSize(change, availableWidth);
        const lines = splitText.length;
        const changeHeight = (lines > 1 ? lines - 1 : lines) * lineHeight;

        doc.text(splitText, startX, startY);
        startY += changeHeight;

        return;
      }

      if (change.changes) {
        doc.text(change.label, currentX, startY);
        startY += GAP / 2;

        change.changes.forEach(printChanges);
        return;
      }

      let prevValue = '';
      let currValue = '';

      if (change.type === 'create') {
        currValue = change.value;
      } else if (['delete', 'update'].includes(change.type)) {
        prevValue = change.prevValue;
        if (change.type === 'update') {
          currValue = change.currValue;
        }
      }

      const fullTextWidth =
        doc.getTextWidth(change.label) +
        (prevValue ? doc.getTextWidth(prevValue) : 0) +
        (currValue ? doc.getTextWidth(currValue) : 0) +
        (currValue && prevValue ? doc.getTextWidth(' from to') : 0);

      const lines = fullTextWidth > availableWidth ? Math.floor(fullTextWidth / availableWidth) : 0;
      const textHeight = lines * lineHeight + smallSpacing;

      drawVerticalLine(
        doc,
        startX + smallSpacing,
        startY - 1.5 * smallSpacing,
        startY + textHeight
      );

      // Draw label
      doc.setFont('helvetica', 'bold');
      doc.text(change.label, currentX, startY);
      currentX += doc.getTextWidth(change.label);
      doc.setFont('helvetica', 'normal');

      // previous value if needed
      if (prevValue) {
        if (currValue) {
          currentX += 1; // Small spacing
          doc.text('from', currentX, startY);
          currentX += doc.getTextWidth('from');
        }

        const { newY, newCurrentX } = drawWrappedText(doc, prevValue, currentX, startY);

        currentX = newCurrentX;
        startY = newY;
      }

      if (currValue) {
        // Check if there is enough space for the current value on the current line
        const { newStartY, newCurrentX } = addNewLineIfNeeded(doc, startY, currentX, currValue);

        startY = newStartY;
        currentX = newCurrentX || changeCurrentX;

        // Draw "to" text if needed
        if (prevValue) {
          if (newCurrentX) currentX += smallSpacing; // Small spacing
          doc.text('to', currentX, startY);
          currentX += doc.getTextWidth('to');
        }

        // current value if needed
        doc.setFont('helvetica', 'bold');
        const { newY } = drawWrappedText(doc, currValue, currentX, startY);
        doc.setFont('helvetica', 'normal');

        startY = newY;
      }

      startY += GAP / 2;
    };

    activity.changes.forEach(printChanges);

    if (activity.isReplyNext) {
      startY += smallSpacing * 2;
    } else {
      const isDateKeyNext = !!formattedActivities?.[index + 1]?.dateKey;

      drawHorizontalLine(doc, startY + (isDateKeyNext ? smallSpacing : 0)); // if date key next, add smallSpacing to the y position to set it as border of dateKey
      // Add spacing between entries
      startY += GAP - lineHeight;
    }
  }

  generatePageNumberAndDate(doc);

  // Return bytes if requested, otherwise open print window
  if (returnBytes) {
    return doc.output('arraybuffer');
  }

  // Use the same print utility as printPdf
  printPrintWindow(doc);
};
